import Phaser from 'phaser';
import { NEON_FONTS } from '@/config/colors';

const POPUP_DURATION_MS = 1200;
const FLOAT_DISTANCE = 30;
const SHAKE_AMPLITUDE = 2;
const SHAKE_FREQUENCY = 15;

interface ActivePopup {
  readonly text: Phaser.GameObjects.Text;
  elapsed: number;
}

/**
 * Manages floating score popups that appear at specific positions,
 * float upward with a shake effect, and fade out.
 */
export class ScorePopupManager {
  private readonly scene: Phaser.Scene;
  private readonly popups: ActivePopup[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  spawn(x: number, y: number, points: number): void {
    const text = this.scene.add.text(x, y, String(points), {
      fontFamily: NEON_FONTS.TITLE,
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#FFE500',
      stroke: '#050520',
      strokeThickness: 2,
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 8, fill: true },
    })
      .setOrigin(0.5)
      .setDepth(150);

    // Scale-in effect
    text.setScale(0.5);
    this.scene.tweens.add({
      targets: text,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 100,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          scaleX: 1,
          scaleY: 1,
          duration: 80,
          ease: 'Sine.easeOut',
        });
      },
    });

    this.popups.push({ text, elapsed: 0 });
  }

  update(delta: number): void {
    for (let i = this.popups.length - 1; i >= 0; i--) {
      const popup = this.popups[i];
      if (!popup) continue;

      popup.elapsed += delta;
      const progress = popup.elapsed / POPUP_DURATION_MS;

      if (progress >= 1) {
        popup.text.destroy();
        this.popups.splice(i, 1);
        continue;
      }

      // Float upward
      const baseY = popup.text.y;
      const floatStep = (FLOAT_DISTANCE * delta) / POPUP_DURATION_MS;
      popup.text.setY(baseY - floatStep);

      // Shake effect (diminishing over time)
      const shakeStrength = SHAKE_AMPLITUDE * (1 - progress);
      const shakeOffset = Math.sin(popup.elapsed * SHAKE_FREQUENCY / 100) * shakeStrength;
      popup.text.setX(popup.text.x + shakeOffset);

      // Fade out in the last 40%
      if (progress > 0.6) {
        const fadeProgress = (progress - 0.6) / 0.4;
        popup.text.setAlpha(1 - fadeProgress);
      }
    }
  }

  destroy(): void {
    for (const popup of this.popups) {
      popup.text.destroy();
    }
    this.popups.length = 0;
  }
}
